import { Button, Card, CardContent } from '@/ui/design-system/components';
import { BodySemi } from 'components/Text';
import CategoryDescriptionCard from 'components/CategoryDescriptionCard';
import DimensionIcon from 'components/DimensionIcon';
import DownloadTimeSeriesButton from 'components/Button/DownloadTimeSeriesButton';
import DrawerBand from 'components/DrawerBand';
import DrawerHeader from 'components/DrawerHeader';
import { DrawerStack } from './styledComponents';
import { buildExportFilename } from 'utils/formatCSV';
import ErrorBoundary from 'containers/ErrorBoundary';
import { FiExternalLink } from 'react-icons/fi';
import { LinkTracked } from 'components/Link';
import Stack from 'components/Stack';
import TikTokHashtagTimeSeries from 'components/DataVis/Charts/TikTokHashtagTimeSeries/TikTokHashtagTimeSeries';
import TikTokTimeSeriesNoDataMessage from 'components/TikTokTimeSeriesNoDataMessage';
import ToggleWatch from 'components/Button/ToggleWatch';
import { TopInstagramHashtagsDrawerSection } from 'components/TopKeywordsSection';
import TopPostsDrawer from 'views/InstagramTrends/InstagramDrawerContent/TopPostsDrawer/TopPostsDrawer';
import { getPathFromNode } from 'utils/helpers';
import { getSubcategoryLabel } from 'utils/hierarchies';
import { useCurrentCountry } from 'utils/hooks';
import useInstagramWeeks from 'utils/hooks/useInstagramWeeks';
import useEndDate from 'utils/hooks/useEndDate';
import { useRef } from 'react';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';
import MetricHeroCard from 'components/MetricHeroCard';
import { DATA_SOURCES } from '@/config/constants';

const CHART_HEIGHT = 287;

function InstagramDrawerContent({
    dataPoint,
    parentHierarchies,
    showAddToWatchlist = true,
}) {
    const theme = useTheme();
    const country = useCurrentCountry();
    const { week, year } = useInstagramWeeks();
    const date = useEndDate('instagram');
    const timeSeriesRef = useRef();
    const {
        name,
        brand,
        category,
        timeSeries,
        hierarchies,
        type: paginateType,
    } = dataPoint;
    const isHashtag = paginateType === 'hashtag';
    // The tables hand down the parent's hierarchies, not the parent itself.
    const subcategory = isHashtag
        ? null
        : getSubcategoryLabel(
              hierarchies,
              parentHierarchies ? { hierarchies: parentHierarchies } : null,
          );

    const timeSeriesName = `${name} (${country?.code || country?.name})`;
    const timeSeriesCsvFilename = buildExportFilename({
        entity: name,
        country,
        source: 'instagram',
        type: paginateType,
        kind: 'time-series',
        date,
    });

    const tsData = [
        {
            id: dataPoint.id,
            name: `Instagram${country ? ` (${country.code || country.name})` : null}`,
            timeSeries,
            type: 'likes',
            color: theme.instagram_orange,
            metricLabel: 'likes',
        },
    ];
    return (
        <Stack $space="1.5rem" css="padding-bottom: 2.25rem;">
            <DrawerHeader
                name={name}
                capitalize={!isHashtag}
                eyebrow={
                    <>
                        <DimensionIcon dimensionType={paginateType} />
                        {paginateType}
                    </>
                }
                contextLabel={subcategory}
            >
                {showAddToWatchlist && !isHashtag && (
                    <ToggleWatch
                        id={paginateType === 'category' ? category : brand}
                        data={{
                            name,
                            type:
                                paginateType === 'category'
                                    ? 'category'
                                    : 'brand',
                        }}
                        btnChildren="Add to watchlist"
                    />
                )}
                {showAddToWatchlist && isHashtag && (
                    <ToggleWatch
                        id={uuid()}
                        data={{
                            name,
                            type: 'instagram-hashtag',
                            [dataPoint.brand ? 'brandId' : 'categoryId']:
                                dataPoint.brand || dataPoint.category,
                        }}
                        btnChildren="Add to watchlist"
                    />
                )}
                {!isHashtag && (
                    <Button variant="default" size="sm" asChild>
                        <LinkTracked
                            to={getPathFromNode(
                                {
                                    id:
                                        paginateType === 'brand'
                                            ? brand
                                            : category,
                                    type: paginateType,
                                    depth:
                                        paginateType === 'brand'
                                            ? null
                                            : 'cat5',
                                },
                                null,
                                'instagram',
                            )}
                            mixpanelContext="DataTableDrawer"
                            mixpanelMeta={{ itemNames: [name] }}
                        >
                            See more data
                            <FiExternalLink />
                        </LinkTracked>
                    </Button>
                )}
            </DrawerHeader>
            <DrawerBand>
                {!isHashtag && (
                    <Card className="overflow-visible">
                        <MetricHeroCard
                            source={DATA_SOURCES.INSTAGRAM}
                            data={dataPoint}
                            healthStats={dataPoint.healthStats}
                            cluster={dataPoint?.cluster}
                        />
                    </Card>
                )}
                <Card>
                    <CardContent className="p-4 flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                            <BodySemi as="span">Likes</BodySemi>
                            <DownloadTimeSeriesButton
                                menuAlign="right"
                                csvFilename={timeSeriesCsvFilename}
                                imageTitle={timeSeriesName}
                                data={tsData}
                                wrapperRef={timeSeriesRef}
                            />
                        </div>
                        <ErrorBoundary>
                            <TikTokHashtagTimeSeries
                                chartHeight={CHART_HEIGHT}
                                data={tsData}
                                isLoading={false}
                                showPlaceholder={
                                    !(timeSeries && timeSeries.length > 0)
                                }
                                placeholderMessage={
                                    !(timeSeries && timeSeries.length > 0) && (
                                        <TikTokTimeSeriesNoDataMessage />
                                    )
                                }
                                wrapperRef={timeSeriesRef}
                            />
                        </ErrorBoundary>
                    </CardContent>
                </Card>
                {paginateType === 'category' ? (
                    <CategoryDescriptionCard
                        categoryId={category}
                        categoryName={name}
                        flavors={dataPoint?.flavors}
                    />
                ) : null}
            </DrawerBand>
            {!isHashtag ? (
                <DrawerStack>
                    <TopInstagramHashtagsDrawerSection
                        enabled={!!((brand || category) && country)}
                        data={{
                            countries: country ? [country.id] : null,
                            brands: brand ? [brand] : null,
                            categories: category ? [category] : null,
                            week,
                            year,
                        }}
                        filename={buildExportFilename({
                            entity: name,
                            country,
                            source: 'instagram',
                            type: 'hashtag',
                            kind: 'data',
                            date,
                        })}
                        paginateBy="hashtags"
                        showIcon={false}
                    />
                </DrawerStack>
            ) : null}
            <DrawerStack>
                <TopPostsDrawer
                    week={week}
                    year={year}
                    countries={country}
                    categories={category}
                    brands={brand}
                    hashtags={dataPoint.hashtag}
                    name={name}
                    displayQty={3}
                    paginateType={paginateType}
                />
            </DrawerStack>
        </Stack>
    );
}

export default InstagramDrawerContent;
